/**
 * Checkbox and radio, drawn rather than left to the browser.
 *
 * The native control is still the thing that is clicked, focused, submitted
 * and announced. It is only its paint that is replaced: `appearance-none`
 * clears the platform box and the classes below draw the Untitled one, with
 * the tick and the dot laid over it as siblings that read the input's state
 * through `peer`. So form participation, `indeterminate`, keyboard and screen
 * reader behaviour are all the browser's own.
 *
 * THE HOVER IS WRITTEN AS AN ARBITRARY VARIANT ON PURPOSE. Tailwind emits
 * `hover:` after `checked:`, so a plain `hover:bg-primary-50` would turn a
 * ticked box pale under the pointer. The hover only applies to a box that is
 * empty and enabled, and says so in its selector.
 */
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'
import { cn } from '../lib/cn'
import { FOCUS_RING } from './control-styles'
import { useFieldContext } from './Field'
import { IconCheck, IconMinus } from './icons'

export type ToggleBoxSize = 'sm' | 'md'

const IDLE_HOVER =
  '[&:not(:checked):not(:indeterminate):enabled:hover]:border-primary-600 ' +
  '[&:not(:checked):not(:indeterminate):enabled:hover]:bg-primary-50'

const BOX_BASE =
  'peer m-0 shrink-0 cursor-pointer appearance-none border border-line-strong bg-surface transition ' +
  IDLE_HOVER +
  ' disabled:cursor-not-allowed disabled:border-line disabled:bg-surface-selected ' +
  FOCUS_RING

const CHECKBOX_SIZES: Record<ToggleBoxSize, string> = {
  sm: 'h-4 w-4 rounded',
  md: 'h-5 w-5 rounded-md',
}

/** The tick and the minus: 12 at sm and 14 at md, stroke 2. */
const GLYPH_SIZES: Record<ToggleBoxSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-3.5 w-3.5',
}

const FILL_PRIMARY =
  'checked:border-primary-600 checked:bg-primary-600 indeterminate:border-primary-600 indeterminate:bg-primary-600'

/** A caller colour, for a chart series checkbox that doubles as its legend. */
const FILL_CUSTOM =
  'checked:border-[color:var(--box-fill)] checked:bg-[color:var(--box-fill)] ' +
  'indeterminate:border-[color:var(--box-fill)] indeterminate:bg-[color:var(--box-fill)]'

const GLYPH = 'pointer-events-none absolute text-white opacity-0 peer-disabled:text-ink-faint'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'color'> {
  label?: ReactNode
  description?: ReactNode
  /** The "some but not all" dash, for a header checkbox over a selectable list. */
  indeterminate?: boolean
  /** 16 or 20. 20 is the measured default. */
  size?: ToggleBoxSize
  /** Overrides the ticked fill, for a series checkbox that is also the legend. */
  color?: string
}

/** The label and description beside a box, shared by both controls. */
function BoxLabel({
  htmlFor,
  label,
  description,
}: {
  htmlFor: string | undefined
  label: ReactNode
  description?: ReactNode
}) {
  return (
    <div className="min-w-0">
      <label htmlFor={htmlFor} className="cursor-pointer text-base font-medium text-ink-secondary">
        {label}
      </label>
      {description ? <p className="text-base text-ink-muted">{description}</p> : null}
    </div>
  )
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { className, label, description, id, indeterminate = false, size = 'md', color, style, ...rest },
  ref,
) {
  const field = useFieldContext()
  const controlId = id ?? field?.id
  // `indeterminate` is a DOM property with no attribute, so it is set on the
  // node. The caller's ref still gets the node.
  const setNode = (node: HTMLInputElement | null): void => {
    if (node !== null) node.indeterminate = indeterminate
    if (typeof ref === 'function') ref(node)
    else if (ref !== null && ref !== undefined) ref.current = node
  }
  const fill = color ? ({ ...style, '--box-fill': color } as CSSProperties) : style
  const box = (
    <span className={cn('relative inline-grid shrink-0 place-items-center', label ? 'mt-0.5' : undefined)}>
      <input
        ref={setNode}
        id={controlId}
        type="checkbox"
        aria-describedby={field?.describedBy}
        aria-checked={indeterminate ? 'mixed' : undefined}
        style={fill}
        className={cn(BOX_BASE, CHECKBOX_SIZES[size], color ? FILL_CUSTOM : FILL_PRIMARY, className)}
        {...rest}
      />
      <IconCheck
        strokeWidth={2}
        className={cn(GLYPH, GLYPH_SIZES[size], 'peer-checked:opacity-100 peer-indeterminate:opacity-0')}
      />
      <IconMinus strokeWidth={2} className={cn(GLYPH, GLYPH_SIZES[size], 'peer-indeterminate:opacity-100')} />
    </span>
  )

  if (!label) return box

  return (
    <div className="flex items-start gap-2">
      {box}
      <BoxLabel htmlFor={controlId} label={label} description={description} />
    </div>
  )
})

const RADIO_SIZES: Record<ToggleBoxSize, string> = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
}

/** The dot: 6 inside the 16 circle, 8 inside the 20. */
const DOT_SIZES: Record<ToggleBoxSize, string> = {
  sm: 'h-1.5 w-1.5',
  md: 'h-2 w-2',
}

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label: ReactNode
  description?: ReactNode
  /** 16 or 20. 16 is the default, which is what a radio list in a form uses. */
  size?: ToggleBoxSize
}

export const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  { className, label, description, id, size = 'sm', ...rest },
  ref,
) {
  const generatedId = id ?? `${rest.name ?? 'radio'}-${String(rest.value)}`
  return (
    <div className="flex items-start gap-2">
      <span className="relative mt-0.5 inline-grid shrink-0 place-items-center">
        <input
          ref={ref}
          id={generatedId}
          type="radio"
          className={cn(
            BOX_BASE,
            RADIO_SIZES[size],
            'rounded-full checked:border-primary-600 checked:bg-primary-50',
            className,
          )}
          {...rest}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute rounded-full bg-primary-600 opacity-0 peer-checked:opacity-100',
            'peer-disabled:bg-ink-faint',
            DOT_SIZES[size],
          )}
        />
      </span>
      <BoxLabel htmlFor={generatedId} label={label} description={description} />
    </div>
  )
})
